import { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { showSuccess, showError } from '@/lib/swal';
import { UnitType, PrimaryUnitAffiliation } from '@/types';
import { User, Plus, Trash2 } from 'lucide-react';

interface ProfileSetupProps {
  onComplete?: () => void;
  isModal?: boolean;
}

export default function ProfileSetup({ onComplete, isModal = true }: ProfileSetupProps) {
  const { currentUser, userProfile, refreshUserProfile } = useAuth();
  const [saving, setSaving] = useState(false);
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [loadingUnitTypes, setLoadingUnitTypes] = useState(true);

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [primaryUnits, setPrimaryUnits] = useState<PrimaryUnitAffiliation[]>([]);

  // Fetch unit types
  useEffect(() => {
    const fetchUnitTypes = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
        
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          const types: UnitType[] = data.unitTypes || [];
          setUnitTypes(types);
          
          if (types.length === 0) {
            console.warn('No unit types configured in admin settings');
          }
        } else {
          console.error('System settings document not found. Please configure unit types in admin.');
          setUnitTypes([]);
        }
      } catch (error) {
        console.error('Error fetching unit types:', error);
        showError('Failed to load unit types. Please try again or contact support.');
        setUnitTypes([]);
      } finally {
        setLoadingUnitTypes(false);
      }
    };

    fetchUnitTypes();
  }, []);

  // Populate form with existing data if available
  useEffect(() => {
    if (userProfile) {
      // Use firstName/lastName if available, otherwise split name
      if (userProfile.firstName) {
        setFirstName(userProfile.firstName);
      } else if (userProfile.name) {
        const parts = userProfile.name.split(' ');
        setFirstName(parts[0] || '');
      }
      if (userProfile.lastName) {
        setLastName(userProfile.lastName);
      } else if (userProfile.name) {
        const parts = userProfile.name.split(' ');
        setLastName(parts.slice(1).join(' ') || '');
      }
      // Convert old single unit format to new format if needed
      if (userProfile.primaryUnits && Array.isArray(userProfile.primaryUnits)) {
        setPrimaryUnits(userProfile.primaryUnits);
      } else if (userProfile.unitName || userProfile.unitNumber) {
        // Legacy data - convert to new format with default unit type
        setPrimaryUnits([{
          unitTypeId: unitTypes[0]?.id || '',
          unitName: userProfile.unitName || '',
          unitNumber: userProfile.unitNumber || '',
          rankId: '',
          rank: '',
        }]);
      }
    }
  }, [userProfile, unitTypes]);

  const handleAddPrimaryUnit = () => {
    setPrimaryUnits([...primaryUnits, {
      unitTypeId: '',
      unitName: '',
      unitNumber: '',
      rankId: '',
      rank: '',
    }]);
  };

  const handleRemovePrimaryUnit = (index: number) => {
    setPrimaryUnits(primaryUnits.filter((_, i) => i !== index));
  };

  const handleUpdatePrimaryUnit = (index: number, field: keyof PrimaryUnitAffiliation, value: string) => {
    const updated = [...primaryUnits];
    updated[index] = { ...updated[index], [field]: value };
    setPrimaryUnits(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser) {
      showError('You must be logged in to update your profile');
      return;
    }

    if (!firstName.trim()) {
      showError('Please enter your first name');
      return;
    }

    if (!lastName.trim()) {
      showError('Please enter your last name');
      return;
    }

    // Validate primary units (optional - no longer required)
    const validPrimaryUnits = primaryUnits.filter(u => 
      u.unitTypeId && u.unitName.trim() && u.rankId
    );

    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        name: `${firstName.trim()} ${lastName.trim()}`, // Keep for backward compatibility
        primaryUnits: validPrimaryUnits,
        // Keep legacy fields for backwards compatibility
        unitName: validPrimaryUnits[0]?.unitName || '',
        unitNumber: validPrimaryUnits[0]?.unitNumber || '',
        profileComplete: true,
      });

      await refreshUserProfile();
      showSuccess('Profile updated successfully!');
      
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      showError('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">First Name *</Label>
          <Input
            id="firstName"
            type="text"
            placeholder="John"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            disabled={saving}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lastName">Last Name *</Label>
          <Input
            id="lastName"
            type="text"
            placeholder="Smith"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            disabled={saving}
          />
        </div>
      </div>
      <p className="text-xs text-muted-foreground -mt-4">
        This will be used in RSVP forms and correspondence
      </p>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Unit Affiliations (RSVP only)</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddPrimaryUnit}
            disabled={saving || loadingUnitTypes || unitTypes.length === 0}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Affiliation
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          For RSVP forms only. Does not grant platform membership or access to unit dashboards.
          To join or manage units on Masonic Tools, use Settings → Memberships.
        </p>

        {loadingUnitTypes ? (
          <div className="text-center py-4">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent mx-auto" />
            <p className="text-xs text-muted-foreground mt-2">Loading unit types...</p>
          </div>
        ) : unitTypes.length === 0 ? (
          <div className="text-center py-8 text-destructive border-2 border-destructive border-dashed rounded-lg bg-destructive/5">
            <p className="text-sm font-medium">No unit types configured</p>
            <p className="text-xs mt-1">Please contact your administrator to configure unit types in the admin panel.</p>
          </div>
        ) : primaryUnits.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
            <p className="text-sm">No unit affiliations added yet</p>
            <p className="text-xs mt-1">Click &quot;Add Affiliation&quot; to pre-fill RSVP forms</p>
          </div>
        ) : (
          <div className="space-y-3">
            {primaryUnits.map((unit, index) => {
              const selectedUnitType = unitTypes.find(ut => ut.id === unit.unitTypeId);
              return (
                <div key={index} className="p-4 border rounded-lg space-y-3 bg-muted/30">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 space-y-3">
                      <div className="space-y-2">
                        <Label htmlFor={`unitType-${index}`}>Order/Type *</Label>
                        <Select
                          value={unit.unitTypeId}
                          onValueChange={(value) => handleUpdatePrimaryUnit(index, 'unitTypeId', value)}
                          disabled={saving}
                        >
                          <SelectTrigger id={`unitType-${index}`}>
                            <SelectValue placeholder="Select order/type" />
                          </SelectTrigger>
                          <SelectContent>
                            {unitTypes.map((ut) => (
                              <SelectItem key={ut.id} value={ut.id}>
                                {ut.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`unitName-${index}`}>
                          {selectedUnitType?.entityName || 'Unit'} Name *
                        </Label>
                        <Input
                          id={`unitName-${index}`}
                          type="text"
                          placeholder={`e.g., Veritas ${selectedUnitType?.entityName || 'Lodge'}`}
                          value={unit.unitName}
                          onChange={(e) => handleUpdatePrimaryUnit(index, 'unitName', e.target.value)}
                          disabled={saving}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`unitNumber-${index}`}>
                          {selectedUnitType?.entityName || 'Unit'} Number *
                        </Label>
                        <Input
                          id={`unitNumber-${index}`}
                          type="text"
                          placeholder="No. 1234"
                          value={unit.unitNumber}
                          onChange={(e) => handleUpdatePrimaryUnit(index, 'unitNumber', e.target.value)}
                          disabled={saving}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`prefix-${index}`}>Prefix *</Label>
                        {selectedUnitType?.ranks && selectedUnitType.ranks.length > 0 ? (
                          <Select
                            value={unit.rankId}
                            onValueChange={(value) => handleUpdatePrimaryUnit(index, 'rankId', value)}
                            disabled={saving || !unit.unitTypeId}
                          >
                            <SelectTrigger id={`prefix-${index}`}>
                              <SelectValue placeholder="Select your prefix" />
                            </SelectTrigger>
                            <SelectContent>
                              {selectedUnitType.ranks.map((r) => (
                                <SelectItem key={r.id} value={r.id}>
                                  {r.name} ({r.shortName})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="flex h-10 w-full items-center rounded-md border border-input bg-muted px-3 py-2 text-sm text-muted-foreground">
                            {!unit.unitTypeId ? 'Select unit type first' : 'No prefixes configured for this unit type'}
                          </div>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`rank-${index}`}>Rank</Label>
                        <Input
                          id={`rank-${index}`}
                          type="text"
                          placeholder="eg. ProvJGW / PPADoC / AGSec"
                          value={unit.rank || ''}
                          onChange={(e) => handleUpdatePrimaryUnit(index, 'rank', e.target.value)}
                          disabled={saving}
                        />
                      </div>
                    </div>
                    {primaryUnits.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemovePrimaryUnit(index)}
                        disabled={saving}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="pt-4">
        <Button type="submit" className="w-full" disabled={saving || loadingUnitTypes}>
          {saving ? 'Saving...' : isModal ? 'Complete Profile' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <User className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle>Complete Your Profile</CardTitle>
              </div>
            </div>
            <CardDescription>
              Provide your details for RSVP forms. Unit affiliations here do not grant platform membership.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {content}
          </CardContent>
        </Card>
      </div>
    );
  }

  return content;
}

